import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

// Task 29 — Cross-tree UI handlers for persona chat.
//
// Links deep-rendered children (CreateArtifactsResult inside a
// MessageBubble's ToolCallPill) to the PersonaContextPanel without
// prop-drilling through 4 layers. Also gives the Panel an imperative
// `focusToolCall` so in-bubble clicks can scroll + flash the
// corresponding Panel row.
//
// Scope: PersonaChat page. Provider lives inside PersonaChatInner so
// leaving the chat unmounts all state + clears the ref pair below.

export type FocusToolCallHandler = (toolCallId: string) => void;
export type ArtifactSelectHandler = (artifactId: string) => void;

export interface PersonaChatUIValue {
  focusToolCall: FocusToolCallHandler;
  onArtifactSelect: ArtifactSelectHandler;
  // Hint to the shell that something in the Panel warrants attention
  // — used by cross-tree clicks (artifact-title in a bubble) to
  // auto-open a collapsed panel before scrolling to the target row.
  requestPanelOpen: () => void;
  // v0.3 Chat DAG: FanCollapsedChip clicks flip membership in
  // ChatDagRuntimeContext.expandedFanIds via a reducer owned by
  // PersonaChatInner. This handler is the stable imperative surface.
  toggleFanExpansion: (toolCallId: string) => void;
  // v0.3 spine click: DAG node click → scroll the corresponding
  // bubble (by data-message-id) into view + flash it. Returns false
  // when the bubble isn't in the DOM — Task 13 adds the toast for
  // that case; for now we log.
  focusBubble: (messageId: string) => boolean;
  // Controls-panel imperatives for the DAG's global-state toggles.
  // Called by the ControlsPanel inside ChatDagCanvas; state lives
  // in PersonaChatInner and flows back through ChatDagRuntimeContext.
  setAllFansCollapsed: (value: boolean) => void;
  setShowPhaseStacks: (value: boolean) => void;
  // Per-turn + global phase-stack collapse imperatives.
  togglePhaseStackCollapse: (turnKey: string) => void;
  setAllPhaseStacksCollapsed: (value: boolean) => void;
}

const noop = () => {};

const PersonaChatUIContext = createContext<PersonaChatUIValue>({
  focusToolCall: noop,
  onArtifactSelect: noop,
  requestPanelOpen: noop,
  toggleFanExpansion: noop,
  focusBubble: () => false,
  setAllFansCollapsed: noop,
  setShowPhaseStacks: noop,
  togglePhaseStackCollapse: noop,
  setAllPhaseStacksCollapsed: noop,
});

// The Panel registers its imperative handlers through these refs;
// the provider dereferences on every call. Ref-based plumbing keeps
// the context value stable (no re-renders of the whole subtree when
// the Panel re-binds its handler).
export interface PanelHandlerRefs {
  focusToolCallRef: React.MutableRefObject<FocusToolCallHandler | null>;
  artifactSelectRef: React.MutableRefObject<ArtifactSelectHandler | null>;
}

export function usePanelHandlerRefs(): PanelHandlerRefs {
  const focusToolCallRef = useRef<FocusToolCallHandler | null>(null);
  const artifactSelectRef = useRef<ArtifactSelectHandler | null>(null);
  return { focusToolCallRef, artifactSelectRef };
}

export const PersonaChatUIProvider: React.FC<{
  children: React.ReactNode;
  handlerRefs: PanelHandlerRefs;
  onRequestPanelOpen?: () => void;
  onToggleFanExpansion?: (toolCallId: string) => void;
  onFocusBubble?: (messageId: string) => boolean;
  onSetAllFansCollapsed?: (value: boolean) => void;
  onSetShowPhaseStacks?: (value: boolean) => void;
  onTogglePhaseStackCollapse?: (turnKey: string) => void;
  onSetAllPhaseStacksCollapsed?: (value: boolean) => void;
}> = ({
  children,
  handlerRefs,
  onRequestPanelOpen,
  onToggleFanExpansion,
  onFocusBubble,
  onSetAllFansCollapsed,
  onSetShowPhaseStacks,
  onTogglePhaseStackCollapse,
  onSetAllPhaseStacksCollapsed,
}) => {
  const focusToolCall = useCallback<FocusToolCallHandler>(
    (id) => {
      handlerRefs.focusToolCallRef.current?.(id);
    },
    [handlerRefs],
  );

  // Open the panel first (if closed), then fire the artifact-select
  // handler on the next frame so the Panel has a chance to be visible
  // before it scrolls to / flashes the row.
  const onArtifactSelect = useCallback<ArtifactSelectHandler>(
    (artifactId) => {
      onRequestPanelOpen?.();
      requestAnimationFrame(() => {
        handlerRefs.artifactSelectRef.current?.(artifactId);
      });
    },
    [handlerRefs, onRequestPanelOpen],
  );

  const requestPanelOpen = useCallback(() => {
    onRequestPanelOpen?.();
  }, [onRequestPanelOpen]);

  const toggleFanExpansion = useCallback(
    (id: string) => {
      onToggleFanExpansion?.(id);
    },
    [onToggleFanExpansion],
  );

  const focusBubble = useCallback(
    (id: string) => onFocusBubble?.(id) ?? false,
    [onFocusBubble],
  );

  const setAllFansCollapsed = useCallback(
    (v: boolean) => onSetAllFansCollapsed?.(v),
    [onSetAllFansCollapsed],
  );

  const setShowPhaseStacks = useCallback(
    (v: boolean) => onSetShowPhaseStacks?.(v),
    [onSetShowPhaseStacks],
  );

  const togglePhaseStackCollapse = useCallback(
    (turnKey: string) => onTogglePhaseStackCollapse?.(turnKey),
    [onTogglePhaseStackCollapse],
  );

  const setAllPhaseStacksCollapsed = useCallback(
    (v: boolean) => onSetAllPhaseStacksCollapsed?.(v),
    [onSetAllPhaseStacksCollapsed],
  );

  const value = useMemo(
    () => ({
      focusToolCall,
      onArtifactSelect,
      requestPanelOpen,
      toggleFanExpansion,
      focusBubble,
      setAllFansCollapsed,
      setShowPhaseStacks,
      togglePhaseStackCollapse,
      setAllPhaseStacksCollapsed,
    }),
    [
      focusToolCall,
      onArtifactSelect,
      requestPanelOpen,
      toggleFanExpansion,
      focusBubble,
      setAllFansCollapsed,
      setShowPhaseStacks,
      togglePhaseStackCollapse,
      setAllPhaseStacksCollapsed,
    ],
  );

  return (
    <PersonaChatUIContext.Provider value={value}>
      {children}
    </PersonaChatUIContext.Provider>
  );
};

export function usePersonaChatUI(): PersonaChatUIValue {
  return useContext(PersonaChatUIContext);
}
